<template>
  <div class="bg-light">
    <b-modal
      ref="initiateFormModal"
      hide-footer
      title="Initiate Student Registration"
      centered
      lazy
      @hide="hideModal"
      size="lg"
    >
      <div>
        <b-alert show variant="light">
          Fill the below form to register a new student. This will send an email to the student for completing the
          registration by entering in complete details. Student will also be able to pay securely through the app and
          apply for sponsorships. Feel free to use this for walk-in registrations or initiating registrations for
          potential students.
        </b-alert>
      </div>
      <b-form class="col-md-12 px-0 page-section pt-0 pb-2" @submit.prevent="onSubmit">
        <div class="row">
          <b-form-group label="First Name*" label-for="firstname" label-class="form-label" class="col-md-6">
            <b-form-input
              id="firstname"
              :placeholder="$t('userMsgs.firstName')"
              v-model.trim="user.firstName"
              :state="!$v.user.firstName.required && $v.user.firstName.$dirty ? false : null"
            />
            <b-form-invalid-feedback v-if="!$v.user.firstName.required"
              >This field is required.</b-form-invalid-feedback
            >
          </b-form-group>

          <b-form-group label="Last Name*" label-for="lastname" label-class="form-label" class="col-md-6">
            <b-form-input
              id="lastname"
              :placeholder="$t('userMsgs.lastName')"
              v-model.trim="user.lastName"
              :state="!$v.user.lastName.required && $v.user.lastName.$dirty ? false : null"
            />
            <b-form-invalid-feedback v-if="!$v.user.lastName.required">This field is required.</b-form-invalid-feedback>
          </b-form-group>
        </div>

        <b-form-group label="Email Address*" label-for="email" label-class="form-label">
          <b-form-input
            id="email"
            :placeholder="$t('authMsgs.emailAddr')"
            type="email"
            v-model.trim="user.email"
            :state="(!$v.user.email.required || !$v.user.email.email) && $v.user.$dirty ? false : null"
          />
          <b-form-invalid-feedback v-if="!$v.user.email.email">
            Invalid email. Please make sure that it's in correct format i.e. test@example.com.</b-form-invalid-feedback
          >
          <b-form-invalid-feedback v-else>This field is required.</b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="Phone" label-for="phone" label-class="form-label">
          <b-form-input
            id="phone"
            placeholder="(888) 689 - 1235"
            v-model="user.phone"
            v-mask="'(###) ### - ####'"
            :state="$v.user.phone.$invalid && $v.user.$dirty ? false : null"
          />
          <b-form-invalid-feedback v-if="$v.user.phone.$invalid && $v.user.$dirty">
            Invalid phone number. Please make sure that it's in correct format i.e. (XXX) XXX - XXXX.
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="Select Payment Plan for student*" label-class="form-label">
          <div class="row no-gutters pay-option-grid">
            <div v-for="option in availablePayOptions" :key="option.value" class="col-md-4 pay-option-col">
              <div
                class="pay-option-card"
                :class="{ 'pay-option-card--active': payment_option === option.value }"
                role="radio"
                tabindex="0"
                :aria-checked="payment_option === option.value ? 'true' : 'false'"
                @click="payment_option = option.value"
                @keydown.space.prevent="payment_option = option.value"
                @keydown.enter.prevent="payment_option = option.value"
              >
                <span class="pay-option-radio">
                  <md-icon v-if="payment_option === option.value">check_circle</md-icon>
                  <md-icon v-else>radio_button_unchecked</md-icon>
                </span>
                <span class="pay-option-icon" :class="`pay-option-icon--${option.value}`">
                  <md-icon>{{ option.icon }}</md-icon>
                </span>
                <h6 class="pay-option-title">{{ option.title }}</h6>
                <p class="pay-option-subtitle">{{ option.subtitle }}</p>
                <div class="pay-option-amount">{{ option.amount }}</div>
                <small class="pay-option-note">{{ option.note }}</small>
              </div>
            </div>
          </div>
          <div v-if="showWaiveFeeToggle" class="row no-gutters pay-option-grid">
            <div class="col-md-4 offset-md-8 pay-option-col waive-fee-col">
              <b-form-checkbox
                id="waiveApplicationFee"
                v-model="waiveApplicationFee"
                name="waiveApplicationFee"
                switch
                class="waive-fee-switch"
              >
                Waive Deposit Fee?
              </b-form-checkbox>
            </div>
          </div>
          <b-form-invalid-feedback :state="!$v.payment_option.required && $v.payment_option.$dirty ? false : null"
            >This field is required.</b-form-invalid-feedback
          >
        </b-form-group>

        <b-btn variant="primary" :disabled="isLoading" style="width: 150px" type="submit" class="btn-normal">
          <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
          <span v-else>add</span>
        </b-btn>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { get } from 'lodash';

import { mapActions } from 'vuex';
import generator from 'generate-password';
import { required, email } from 'vuelidate/lib/validators';
import { PHONE_FORMAT_REGEX, STD_REG_PAY_OPTIONS } from '../../../../common/constants';
import { titleize } from '../../../../common/utils';

const PAY_OPTION_CARDS = [
  {
    value: STD_REG_PAY_OPTIONS.FREE_PLAN,
    icon: 'card_giftcard',
    title: 'Free/ Sponsored',
    subtitle: 'No payment required.',
    amount: 'No charge',
    note: ''
  },
  {
    value: STD_REG_PAY_OPTIONS.PAY_LATER,
    icon: 'event',
    title: 'Deposit Paid',
    subtitle: 'Deposit already collected off-platform. Tuition placed on a payment plan.'
  },
  {
    value: STD_REG_PAY_OPTIONS.PAY_NOW,
    icon: 'credit_card',
    title: 'Pay in Full',
    subtitle: 'Remaining balance due now.'
  }
];

export default {
  props: {
    showModal: { type: Boolean, default: false },
    selectedClass: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      isLoading: false,
      user: {
        firstName: '',
        lastName: '',
        email: '',
        phone: ''
      },

      waiveApplicationFee: false,
      programDetails: null,
      payment_option: STD_REG_PAY_OPTIONS.PAY_NOW
    };
  },
  computed: {
    applicationFee() {
      return this.waiveApplicationFee ? 0 : Number(get(this.programDetails, 'application_fee', 0)) || 0;
    },
    // 'Deposit Paid' means the school already took the deposit, so the waiver is
    // part of the option rather than a separate switch the admin has to find.
    depositAlreadyPaid() {
      return this.payment_option === STD_REG_PAY_OPTIONS.PAY_LATER;
    },
    // Waiving is only a choice when the student is being charged in full; the
    // other two options already imply their own answer.
    showWaiveFeeToggle() {
      return this.payment_option === STD_REG_PAY_OPTIONS.PAY_NOW;
    },
    tuitionFee() {
      return Number(get(this.programDetails, 'tuition_fee', 0)) || 0;
    },
    availablePayOptions() {
      const options = PAY_OPTION_CARDS;

      // fees come from the program fetch on open; until it lands there's no amount to show
      return options.map(opt => {
        if (opt.value === STD_REG_PAY_OPTIONS.PAY_NOW) {
          const note = this.applicationFee ? 'Tuition Fee + Deposit Fee' : 'Tuition Fee';
          return {
            ...opt,
            amount: this.programDetails ? this.formatCurrency(this.tuitionFee + this.applicationFee) : '',
            note: this.programDetails ? note : ''
          };
        }
        if (opt.value === STD_REG_PAY_OPTIONS.PAY_LATER) {
          return {
            ...opt,
            amount: this.programDetails ? this.formatCurrency(this.tuitionFee) : '',
            note: this.programDetails ? 'tuition on payment plan' : ''
          };
        }
        return { ...opt };
      });
    },
  },
  validations() {
    return {
      payment_option: { required },
      user: {
        firstName: { required },
        lastName: { required },
        email: { required, email },
        phone: {
          isCorrectFormat(value) {
            return value === '' || PHONE_FORMAT_REGEX.test(value);
          }
        }
      }
    };
  },
  watch: {
    // the toggle only exists for Pay in Full, so don't let a waiver the admin
    // can no longer see follow them onto another option
    payment_option(value) {
      if (value !== STD_REG_PAY_OPTIONS.PAY_NOW) {
        this.waiveApplicationFee = false;
      }
    },
    showModal(value) {
      if (value) {
        this.resetData();
        this.$v.$reset();
        this.fetchProgramFees();
        this.$refs.initiateFormModal.show();
      }
    }
  },
  methods: {
    ...mapActions('student', ['initiateStudentRegistration']),
    ...mapActions('program', ['getProgram']),
    get,
    hideModal() {
      this.$emit('close');
    },
    hideModalManual() {
      this.$refs.initiateFormModal.hide();
      this.hideModal();
    },
    resetData() {
      this.user.email = '';
      this.user.firstName = '';
      this.user.lastName = '';
      this.waiveApplicationFee = false;
      this.programDetails = null;
      this.user.phone = '';
      this.payment_option = STD_REG_PAY_OPTIONS.PAY_NOW;
      this.isLoading = false;
    },
    hideUpdateModal() {
      this.resetData();
      this.$refs.initiateFormModal.hide();
      this.$emit('closeSubmit');
    },
    async onSubmit() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.isLoading = true;
        try {
          const data = {
            // birthday: removed from initiate registration payload
            waive_app_fee: this.depositAlreadyPaid || this.waiveApplicationFee,
            registration: {
              program: this.selectedClass.program.id,
              class_enrolled: this.selectedClass.id,
              payment_option: this.payment_option
            },
            user: {
              email: this.user.email,
              first_name: titleize(this.user.firstName),
              last_name: titleize(this.user.lastName),
              password: generator.generate({ length: 10, numbers: true, strict: true }),
              phone: this.user.phone
              // gender: removed from initiate registration payload
            }
          };
          await this.initiateStudentRegistration({ ...data });
          setTimeout(
            () =>
              this.makeToast({
                variant: 'success',
                msg: 'Registration Initiated Successfully!'
              }),
            250
          );
          this.hideUpdateModal();
        } catch (err) {
          if (err.response.status === 400) {
            this.makeToast({ variant: 'danger', msg: 'User with this email already exists.' });
            this.isLoading = false;
          } else {
            this.makeToast({
              variant: 'danger',
              msg: 'Cannot register in this class. Please contact administrator.'
            });
          }
        }
      } else {
        this.makeToast({ variant: 'danger', msg: 'Please fill all fields correctly.' });
      }
      this.isLoading = false;
    },
    async fetchProgramFees() {
      const programId = get(this.selectedClass, 'program.id');
      if (!programId) return;
      try {
        this.programDetails = get(await this.getProgram(programId), 'data', null);
      } catch (e) {
        // without the fees the payment plan options simply show no amount
      }
    },
    formatCurrency(value) {
      const number = Number(value) || 0;
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(number);
    }
  }
};
</script>

<style scoped>
.pay-option-grid {
  margin: 0 -8px;
}
.pay-option-col {
  padding: 0 8px;
  margin-bottom: 12px;
}
.pay-option-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  padding: 20px 16px 16px;
  text-align: center;
  border: 1px solid #e0e5ec;
  border-radius: 14px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease-in-out, background-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}
.pay-option-card:hover {
  border-color: #b9c4d4;
}
.pay-option-card:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.15);
}
.pay-option-card--active {
  border-color: #007bff;
  background: #eef6ff;
}
.pay-option-radio {
  position: absolute;
  top: 12px;
  right: 12px;
  line-height: 1;
  color: #c3cbd6;
}
.pay-option-radio >>> .material-icons {
  font-size: 22px;
}
.pay-option-card--active .pay-option-radio {
  color: #007bff;
}
.pay-option-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  margin-bottom: 14px;
  border-radius: 12px;
  background: #f0f3f7;
  color: #5b6b7f;
}
.pay-option-icon >>> .material-icons {
  font-size: 24px;
}
.pay-option-card--active .pay-option-icon {
  background: #dbeafe;
  color: #007bff;
}
.pay-option-icon--free_plan,
.pay-option-card--active .pay-option-icon--free_plan {
  background: #e6f6ec;
  color: #28a745;
}
.pay-option-title {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 1.5rem;
  margin-bottom: 6px;
  font-size: 1rem;
  font-weight: 700;
  color: #1b2733;
}
.pay-option-subtitle {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 2.75rem;
  margin-bottom: 14px;
  font-size: 0.8125rem;
  line-height: 1.35;
  color: #868e96;
}
.pay-option-amount {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 1.75rem;
  margin-top: auto;
  font-size: 1.25rem;
  font-weight: 700;
  color: #007bff;
}
.pay-option-card:not(.pay-option-card--active) .pay-option-amount {
  color: #3c4858;
}
.pay-option-note {
  display: block;
  min-height: 1.125rem;
  font-size: 0.75rem;
  line-height: 1.2;
  color: #adb5bd;
}
.waive-fee-col {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 4px;
  margin-bottom: 0;
}
.waive-fee-switch >>> .custom-control-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #3c4858;
}
</style>
